import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeDollarSign,
  Building2,
  CheckCircle2,
  Gauge,
  Layers3,
  ShieldCheck,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useSettings } from "@/features/settings/contexts/SettingsContext";
import { useAuth } from "@/features/auth/AuthContext";
import { cn } from "@/ui/theme/ThemeContext";

type PlanId = "free" | "pro" | "ultra";

const plans: Array<{
  id: PlanId;
  name: string;
  price: string;
  cadence: string;
  summary: string;
  features: string[];
  capacity: string;
  badge?: string;
  icon: LucideIcon;
  accent: string;
  cardClass: string;
  buttonClass: string;
}> = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    cadence: "forever",
    summary: "Start one launch plan with core workspace views.",
    features: ["1 workspace", "100 tasks", "5 docs", "3 reports"],
    capacity: "Starter workspace",
    icon: Layers3,
    accent: "from-zinc-500 to-slate-400",
    cardClass:
      "border-white/[0.12] bg-zinc-950/[0.88] text-white shadow-[0_20px_70px_rgba(0,0,0,0.28)]",
    buttonClass:
      "border border-white/[0.14] bg-white/[0.06] text-white hover:border-white/25 hover:bg-white/[0.11]",
  },
  {
    id: "pro",
    name: "Pro",
    price: "$20/account/month",
    cadence: "launch team",
    summary: "Recommended for turning active project work into a launch plan.",
    features: ["10 workspaces", "5k tasks", "500 docs and reports", "Exports planned"],
    capacity: "Best for active launches",
    badge: "Recommended",
    icon: Gauge,
    accent: "from-blue-500 via-cyan-400 to-emerald-400",
    cardClass:
      "border-transparent bg-[linear-gradient(145deg,rgba(37,99,235,0.18),rgba(20,184,166,0.12)_46%,rgba(255,255,255,0.84))] text-zinc-950 shadow-[0_24px_80px_rgba(37,99,235,0.26)] ring-1 ring-blue-400/45 dark:bg-[linear-gradient(145deg,rgba(37,99,235,0.28),rgba(20,184,166,0.12)_48%,rgba(255,255,255,0.065))] dark:text-white dark:shadow-[0_26px_90px_rgba(37,99,235,0.35)] dark:ring-cyan-300/35 lg:-mt-3 lg:min-h-[438px]",
    buttonClass:
      "bg-[linear-gradient(135deg,#2563eb,#06b6d4_55%,#10b981)] text-white shadow-[0_14px_34px_rgba(37,99,235,0.34)] hover:shadow-[0_18px_44px_rgba(6,182,212,0.34)]",
  },
  {
    id: "ultra",
    name: "Ultra",
    price: "$200/account/month",
    cadence: "scale operators",
    summary: "Higher usage for operators who need more launch-plan capacity.",
    features: ["50 workspaces", "50k tasks", "Priority support", "Advanced AI and automation planned"],
    capacity: "Priority operating room",
    icon: BadgeDollarSign,
    accent: "from-violet-500 via-blue-400 to-amber-300",
    cardClass:
      "border-white/[0.12] bg-zinc-950/[0.88] text-white shadow-[0_20px_70px_rgba(0,0,0,0.28)]",
    buttonClass:
      "border border-white/[0.14] bg-white/[0.06] text-white hover:border-white/25 hover:bg-white/[0.11]",
  },
];

export const PricingPage: React.FC = () => {
  const { t } = useSettings();
  const { isAuthenticated, isLoading } = useAuth();
  // A signed-in visitor (typically an organization admin arriving from the header "Upgrade" button)
  // cannot sign up again; the signup links only dead-end at "Already signed in".
  const isSignedIn = !isLoading && isAuthenticated;

  return (
    <div
      data-app-ready="true"
      data-active-view="public-pricing"
      className="min-h-screen overflow-hidden bg-[linear-gradient(135deg,#f8fafc_0%,#eef2ff_42%,#ecfeff_100%)] px-5 py-7 text-zinc-950 dark:bg-[linear-gradient(135deg,#121212_0%,#171717_50%,#0f1a19_100%)] dark:text-white sm:px-8"
    >
      <main className="mx-auto w-full max-w-[1080px]">
        <header className="flex min-w-0 flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 pt-1">
            <Link
              className="inline-flex text-sm font-medium text-blue-700 underline underline-offset-4 transition-colors duration-200 hover:text-blue-900 dark:text-cyan-300 dark:hover:text-cyan-100"
              to={isSignedIn ? "/" : "/signup?returnTo=%2F"}
            >
              {t(isSignedIn ? "Back to app" : "Back to signup")}
            </Link>
            <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-tight text-zinc-950 dark:text-white sm:text-5xl">
              {t("Management Tool pricing")}
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-zinc-700 dark:text-zinc-300 sm:text-base sm:leading-7">
              {t("Choose a self-service plan for one owner account. Free, Pro, and Ultra include Dashboard, Gantt, Kanban, Calendar, Docs, and Reports.")}
            </p>
            <div className="mt-5 flex min-w-0 flex-wrap gap-2">
              {["No card on signup", "Shadow preference", "Core views included"].map((label) => (
                <span
                  key={label}
                  className="inline-flex min-h-[32px] items-center rounded-md border border-zinc-300/70 bg-white/70 px-3 text-xs font-semibold text-zinc-700 shadow-[0_8px_24px_rgba(15,23,42,0.06)] backdrop-blur dark:border-white/[0.10] dark:bg-white/[0.06] dark:text-zinc-200"
                >
                  {t(label)}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-blue-200/80 bg-white/[0.76] px-4 py-3 text-sm shadow-[0_18px_48px_rgba(37,99,235,0.10)] backdrop-blur-xl dark:border-cyan-300/[0.15] dark:bg-white/[0.065] dark:shadow-[0_18px_60px_rgba(6,182,212,0.10)] lg:max-w-sm">
            <div className="flex min-w-0 items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-cyan-300" aria-hidden="true" />
              <p className="max-w-xs leading-6 text-zinc-700 dark:text-zinc-300">
                {t("CommerceOS checkout is not active yet. No card is collected on signup, and plan choice stays a shadow preference.")}
              </p>
            </div>
          </div>
        </header>

        <section className="mt-10 grid gap-4 lg:grid-cols-3 lg:items-stretch" aria-label={t("Self-service plans")}>
          {plans.map((plan) => {
            const Icon = plan.icon;
            const hasDarkSurface = plan.id !== "pro";
            // Translate the whole price ("$20/account/month" → "$20/계정/월") before splitting it for layout.
            const localizedPrice = t(plan.price);
            const priceSeparator = localizedPrice.indexOf("/");
            const priceLead = priceSeparator === -1 ? localizedPrice : localizedPrice.slice(0, priceSeparator);
            const priceRest = priceSeparator === -1 ? "" : localizedPrice.slice(priceSeparator);

            return (
              <article
                key={plan.id}
                className={cn(
                  "group relative flex min-w-0 flex-col overflow-hidden rounded-lg border p-5 backdrop-blur-xl transition-all duration-300 ease-out hover:-translate-y-1 hover:scale-[1.012] focus-within:ring-2 focus-within:ring-blue-400/60 sm:p-6",
                  plan.cardClass
                )}
              >
                <div className={cn("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", plan.accent)} aria-hidden="true" />
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div
                    className={cn(
                      "flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br text-white shadow-[0_10px_28px_rgba(15,23,42,0.16)]",
                      plan.accent
                    )}
                    aria-hidden="true"
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  {plan.badge ? (
                    <div className="inline-flex min-h-[30px] items-center gap-1 rounded-md bg-white px-2.5 text-xs font-semibold text-blue-700 shadow-[0_10px_24px_rgba(37,99,235,0.16)] dark:bg-cyan-300/[0.15] dark:text-cyan-100">
                      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                      {t(plan.badge)}
                    </div>
                  ) : null}
                </div>
                <p className={cn("mt-5 text-xs font-semibold uppercase", hasDarkSurface ? "text-zinc-400" : "text-zinc-500 dark:text-zinc-400")}>
                  {t(plan.capacity)}
                </p>
                <h2 className={cn("mt-2 text-2xl font-semibold", hasDarkSurface ? "text-white" : "text-zinc-950 dark:text-white")}>
                  {t(plan.name)}
                </h2>
                <div className="mt-3 flex min-w-0 flex-wrap items-end gap-x-2 gap-y-1">
                  <span className="sr-only">{localizedPrice}</span>
                  <span
                    aria-hidden="true"
                    className={cn("text-4xl font-semibold leading-none", hasDarkSurface ? "text-white" : "text-zinc-950 dark:text-white")}
                  >
                    {priceLead}
                  </span>
                  <span
                    aria-hidden="true"
                    className={cn("pb-1 text-sm font-medium", hasDarkSurface ? "text-zinc-300" : "text-zinc-600 dark:text-zinc-300")}
                  >
                    {priceRest || t(plan.cadence)}
                  </span>
                </div>
                <p className={cn("mt-4 min-h-[56px] text-sm leading-6", hasDarkSurface ? "text-zinc-300" : "text-zinc-700 dark:text-zinc-300")}>
                  {t(plan.summary)}
                </p>
                <ul className="mt-5 grid gap-3">
                  {plan.features.map((feature) => (
                    <li
                      key={feature}
                      className={cn("flex min-w-0 items-start gap-2 text-sm", hasDarkSurface ? "text-zinc-300" : "text-zinc-700 dark:text-zinc-300")}
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500 dark:text-emerald-300" aria-hidden="true" />
                      <span className="leading-5">{t(feature)}</span>
                    </li>
                  ))}
                </ul>
                {isSignedIn ? null : (
                  <Link
                    className={cn(
                      "mt-auto inline-flex h-11 min-h-[44px] items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-cyan-300/60 dark:focus:ring-offset-zinc-950",
                      plan.buttonClass
                    )}
                    to={`/signup?plan=${plan.id}&returnTo=%2F`}
                  >
                    {t(plan.id === "free" ? "Start free" : "Choose plan")}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                )}
              </article>
            );
          })}
        </section>

        {isSignedIn ? (
          <section
            data-testid="pricing-signed-in-notice"
            className="mt-5 flex min-w-0 flex-col gap-3 rounded-lg border border-blue-200/80 bg-white/[0.76] p-5 text-sm shadow-[0_18px_48px_rgba(37,99,235,0.10)] backdrop-blur-xl dark:border-cyan-300/[0.15] dark:bg-white/[0.065] sm:flex-row sm:items-center sm:justify-between"
          >
            <p className="leading-6 text-zinc-700 dark:text-zinc-300">
              {t("You are signed in. Self-service owners change plans from Account and Plan in the app header; organization workspaces are handled through Enterprise sales.")}
            </p>
            <Link
              className="inline-flex h-11 min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-md bg-[linear-gradient(135deg,#2563eb,#06b6d4_55%,#10b981)] px-4 py-2 text-sm font-semibold text-white shadow-[0_14px_34px_rgba(37,99,235,0.34)] transition-all duration-300 hover:shadow-[0_18px_44px_rgba(6,182,212,0.34)] focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-cyan-300/60 dark:focus:ring-offset-zinc-950"
              to="/"
            >
              {t("Back to app")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </section>
        ) : null}

        <section className="mt-5 rounded-lg border border-zinc-200/80 bg-white/[0.76] p-5 shadow-[0_18px_50px_rgba(15,23,42,0.07)] backdrop-blur-xl dark:border-white/[0.10] dark:bg-white/[0.055] dark:shadow-[0_20px_70px_rgba(0,0,0,0.24)]">
          <div className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-center">
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-2">
                <Building2 className="h-5 w-5 shrink-0 text-blue-600 dark:text-cyan-300" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-zinc-950 dark:text-white">{t("Enterprise")}</h2>
              </div>
              <p className="mt-2 text-sm leading-6 text-zinc-700 dark:text-zinc-300">
                {t("Enterprise is contact-sales only for organizations that need domain users, seat billing, role management, procurement, and admin configuration.")}
              </p>
            </div>
            <a
              className="inline-flex h-11 min-h-[44px] items-center justify-center gap-2 rounded-md border border-blue-300/80 bg-blue-50/80 px-4 py-2 text-sm font-semibold text-blue-700 transition-all duration-300 hover:border-blue-400 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:ring-offset-2 focus:ring-offset-white dark:border-white/[0.14] dark:bg-white/[0.06] dark:text-white dark:hover:border-white/25 dark:hover:bg-white/[0.11] dark:focus:ring-cyan-300/60 dark:focus:ring-offset-zinc-950"
              href="mailto:sales@dope-ai.kr?subject=Management%20Tool%20Enterprise"
            >
              {t("Contact sales")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </section>

        <p className="mt-5 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
          {t("Prices are displayed for the planned self-service billing launch. Paid entitlements remain inactive until CommerceOS registration, provider credentials, webhook proof, billing owner binding, and deployment configuration are complete.")}
        </p>
      </main>
    </div>
  );
};
